import { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    TextField,
    Button,
    Alert,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
} from '@mui/material';
import {
    AccountBalance,
    Payment,
    Person,
    AttachMoney,
} from '@mui/icons-material';
import { customerAPI, virtualBankAccountAPI } from '../services/api';
import { formatCurrency } from '../utils/formatCurrency';

const PaymentSimulation = () => {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [virtualBankAccount, setVirtualBankAccount] = useState(null);
    const [amount, setAmount] = useState('');
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [simulating, setSimulating] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [simulationResult, setSimulationResult] = useState(null);

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async () => {
        try {
            setLoading(true);
            const response = await customerAPI.getAll();
            setCustomers(response.data.customers || []);
        } catch (err) {
            setError('Failed to fetch customers');
            console.error('Fetch customers error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerChange = async (customerId) => {
        setSelectedCustomer(customerId);
        setVirtualBankAccount(null);
        setError('');
        setSuccess('');
        setSimulationResult(null);

        if (customerId) {
            try {
                const response = await virtualBankAccountAPI.getByCustomer(customerId);
                setVirtualBankAccount(response.data);
            } catch (err) {
                // Virtual bank account doesn't exist yet
                console.log('No virtual bank account found for customer');
            }
        }
    };

    const createVirtualBankAccount = async () => {
        if (!selectedCustomer) {
            setError('Please select a customer first');
            return;
        }

        try {
            setCreating(true);
            setError('');
            const response = await virtualBankAccountAPI.create({
                customerId: selectedCustomer
            });
            setVirtualBankAccount(response.data.virtualBankAccount);
            setSuccess('Virtual bank account created successfully!');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create virtual bank account');
            console.error('Create virtual bank account error:', err);
        } finally {
            setCreating(false);
        }
    };

    const simulatePayment = async () => {
        if (!selectedCustomer) {
            setError('Please select a customer first');
            return;
        }

        if (!amount || parseFloat(amount) <= 0) {
            setError('Please enter a valid amount');
            return;
        }

        try {
            setSimulating(true);
            setError('');
            const response = await virtualBankAccountAPI.simulatePayment({
                customerId: selectedCustomer,
                amount: Math.round(parseFloat(amount) * 100) // Convert to cents
            });
            setSimulationResult(response.data);
            setSuccess('Payment simulation completed successfully!');

            // Refresh virtual bank account data to show updated balance
            try {
                const accountResponse = await virtualBankAccountAPI.getByCustomer(selectedCustomer);
                setVirtualBankAccount(accountResponse.data);
            } catch (err) {
                console.log('Failed to refresh virtual bank account data');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to simulate payment');
            console.error('Simulate payment error:', err);
        } finally {
            setSimulating(false);
        }
    };


    const selectedCustomerData = customers.find(c => c._id === selectedCustomer);

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                ACH Payment Simulation
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Simulate ACH payments using Stripe test helpers to test your payment flow.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Customer Selection */}
                <Grid item xs={12} md={6}>
                    <Paper variant='outlined' sx={{ p: 3, height: "100%" }}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Person sx={{ mr: 1 }} />
                            <Typography variant="h6">Select Customer</Typography>
                        </Box>

                        <FormControl fullWidth>
                            <InputLabel>Customer</InputLabel>
                            <Select
                                value={selectedCustomer}
                                onChange={(e) => handleCustomerChange(e.target.value)}
                                label="Customer"
                            >
                                {customers.map((customer) => (
                                    <MenuItem key={customer._id} value={customer._id}>
                                        {customer.firstName} {customer.lastName} ({customer.email})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {selectedCustomerData && (
                            <Card variant='outlined' sx={{ mt: 2 }}>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Selected Customer
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedCustomerData.firstName} {selectedCustomerData.lastName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedCustomerData.email}
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                    </Paper>
                </Grid>

                {/* Virtual Bank Account */}
                <Grid item xs={12} md={6}>
                    <Paper variant='outlined' sx={{ p: 3 }}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <AccountBalance sx={{ mr: 1 }} />
                            <Typography variant="h6">Virtual Bank Account</Typography>
                        </Box>

                        {virtualBankAccount ? (
                            <Card variant='outlined'>
                                <CardContent>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Account Details
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>Bank:</strong> {virtualBankAccount.bankName}
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>Account Holder:</strong> {virtualBankAccount.accountHolderName}
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>Routing Number:</strong> {virtualBankAccount.routingNumber}
                                    </Typography>
                                    <Typography variant="body1">
                                        <strong>Account Number:</strong> {virtualBankAccount.accountNumber}
                                    </Typography>
                                    {virtualBankAccount?.balance?.currentBalance ? (
                                        <>
                                            <Typography variant="body1">
                                                <strong>Current Balance:</strong> {formatCurrency(virtualBankAccount.balance.currentBalance, virtualBankAccount.balance.currency)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Last Updated: {virtualBankAccount?.balance?.lastSyncedAt ? new Date(virtualBankAccount.balance.lastSyncedAt).toLocaleString() : 'Never'}
                                            </Typography>
                                        </>
                                    ) : null}
                                </CardContent>
                            </Card>
                        ) : (
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    No virtual bank account found for this customer.
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={createVirtualBankAccount}
                                    disabled={!selectedCustomer || creating}
                                    startIcon={creating ? <CircularProgress size={20} /> : <AccountBalance />}
                                >
                                    {creating ? 'Creating...' : 'Create Virtual Bank Account'}
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Grid>

                {/* Payment Simulation */}
                <Grid item xs={12}>
                    <Paper variant='outlined' sx={{ p: 3 }}>
                        <Box display="flex" alignItems="center" mb={2}>
                            <Payment sx={{ mr: 1 }} />
                            <Typography variant="h6">Simulate Payment</Typography>
                        </Box>

                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    fullWidth
                                    label="Amount"
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <AttachMoney />
                                            </InputAdornment>
                                        ),
                                    }}
                                    placeholder="Enter amount in USD"
                                    inputProps={{ min: 0, step: 0.01 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={simulatePayment}
                                    disabled={!selectedCustomer || !amount || simulating}
                                    startIcon={simulating ? <CircularProgress size={20} /> : <Payment />}
                                    size="large"
                                >
                                    {simulating ? 'Simulating...' : 'Simulate Payment'}
                                </Button>
                            </Grid>
                        </Grid>

                        {simulationResult && (
                            <Box sx={{ mt: 3 }}>
                                <Divider sx={{ mb: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Simulation Result
                                </Typography>
                                <Card>
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Transaction ID
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                                                    {simulationResult.transaction?.id}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Amount
                                                </Typography>
                                                <Typography variant="body1">
                                                    {formatCurrency(simulationResult.amount)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Currency
                                                </Typography>
                                                <Typography variant="body1">
                                                    {simulationResult.currency?.toUpperCase()}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Customer
                                                </Typography>
                                                <Typography variant="body1">
                                                    {simulationResult.customer?.name}
                                                </Typography>
                                            </Grid>
                                            {simulationResult.currentBalance && (
                                                <Grid item xs={12} sm={6}>
                                                    <Typography variant="subtitle2" color="text.secondary">
                                                        Updated Balance
                                                    </Typography>
                                                    <Typography variant="body1" color="success.main">
                                                        {formatCurrency(simulationResult.currentBalance)}
                                                    </Typography>
                                                </Grid>
                                            )}
                                            <Grid item xs={12}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Status
                                                </Typography>
                                                <Typography variant="body1" color="success.main">
                                                    {simulationResult.message}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default PaymentSimulation;